/**
 * C05 Day-1 Hook — home page entry banner ("禮包" menu).
 *
 * Surfaces three retention CTAs on the home page header:
 *   1. 7-day login calendar (with red-dot when pendingDays > 0)
 *   2. First-launch gift overlay (the FIRST time the kid lands on home)
 *   3. Tutorial-boss CTA (when not yet completed) — fires `ef:start-tutorial-boss`
 *      window event; main.ts routes to the new boss arena. (Wire-up deferred;
 *      the event is dispatched even if no listener, so the banner is safe to
 *      ship before the scene integration lands.)
 *
 * The banner auto-hides when `snapshotHook(p).fullyComplete` — once kids have
 * claimed all 7 days AND beaten the tutorial boss, the retention hook has
 * served its purpose and the home page returns to its usual layout.
 *
 * NOTE: This module does NOT directly modify combat code or the existing
 * 9-boss config. It only emits events; scene-side handlers (to be added
 * later) can opt-in.
 */

import type { PlayerProgress } from '../shared/types';
import { saveProgress } from '../shared/progress';
import {
  snapshotHook,
  seedFirstLaunchIfNeeded,
  claimDay,
  FIRST_LAUNCH_GIFT_XP,
  DAY_REWARDS,
} from './dayone-hook';
import { renderDayOneCalendar } from './dayone-calendar';

export interface DayOneBannerActions {
  /** Called when the kid taps the 教學魔王 button. Optional — falls back to a window event. */
  onStartTutorialBoss?: () => void;
  /**
   * Override Date.now() for deterministic tests. When provided, the banner
   * uses this value for every snapshot/seed call AND disables the midnight
   * refresh tick (tests drive time manually). In production this is omitted
   * so the banner re-reads `Date.now()` on every render — critical for the
   * Day-N+1 unlock to surface even if the kid leaves the page open across
   * midnight (9-yo kids don't refresh manually). QA #263 fix #2.
   */
  nowMs?: number;
  /**
   * Override the midnight refresh interval (ms). Defaults to 60s. Set to 0
   * to disable the tick entirely (tests should also pass `nowMs` instead).
   */
  refreshIntervalMs?: number;
}

/**
 * Mount the banner into `container`. If the player is "fullyComplete" the
 * banner is a no-op (renders nothing). Returns an unmount function.
 *
 * Side effect: calls `seedFirstLaunchIfNeeded(progress)` on first call so the
 * 7-day arc starts ticking from the moment the kid first sees the home page.
 * Persists via saveProgress if seeding mutated the progress object.
 *
 * QA #263 fix #2: when `actions.nowMs` is undefined the banner sets a 60s
 * `setInterval` that re-renders against a fresh `Date.now()`. This is how the
 * D2 reward becomes claimable to a kid who leaves the home page open past
 * midnight — without it the snapshot would stay frozen on the launch-day
 * date and the kid would never see the new pending day until they refreshed.
 */
export function renderDayOneBanner(
  container: HTMLElement,
  progress: PlayerProgress,
  actions: DayOneBannerActions = {},
): () => void {
  // When `actions.nowMs` is provided (tests) we use it verbatim and skip the
  // refresh tick. In production we read Date.now() lazily on every render so
  // the snapshot stays current across midnight without manual refresh.
  const useLiveClock = actions.nowMs === undefined;
  const getNow = (): number => (useLiveClock ? Date.now() : actions.nowMs!);
  const refreshIntervalMs = actions.refreshIntervalMs ?? 60_000;

  // Seed first-launch timestamp lazily (idempotent — no-op once set).
  if (seedFirstLaunchIfNeeded(progress, getNow())) {
    saveProgress(progress);
  }

  const root = document.createElement('div');
  root.className = 'dayone-banner';
  root.setAttribute('data-testid', 'dayone-banner');

  let calendarUnmount: (() => void) | null = null;
  let overlayUnmount: (() => void) | null = null;
  let refreshTimer: ReturnType<typeof setInterval> | null = null;

  function destroy(): void {
    calendarUnmount?.();
    overlayUnmount?.();
    if (refreshTimer !== null) {
      clearInterval(refreshTimer);
      refreshTimer = null;
    }
    root.remove();
  }

  function rerender(): void {
    const nowMs = getNow();
    const snap = snapshotHook(progress, nowMs);
    if (snap.fullyComplete) {
      // Retention hook done — collapse the banner entirely.
      root.innerHTML = '';
      root.classList.add('dayone-banner--hidden');
      return;
    }
    // De-dup fix (boss「簽到那些弄得很爛」): on first launch the body-level
    // welcome-gift overlay (mountFirstLaunchOverlay) IS the single onboarding 禮包
    // moment. Showing the inline banner at the same time produced TWO competing
    // 🎁 banners (「歡迎加入…領取禮包」overlay + 「新手禮包 7 個獎勵待領」inline). While
    // the welcome overlay is pending (= D1 unclaimed) we collapse the inline banner
    // so the kid sees exactly one gift. The inline 每日簽到 entry surfaces only AFTER
    // the welcome gift is dealt with (D1 claimed), as the recurring sign-in entry.
    if (snap.showGiftOverlay) {
      root.innerHTML = '';
      root.classList.add('dayone-banner--hidden');
      return;
    }
    root.classList.remove('dayone-banner--hidden');
    const pending = snap.pendingDays.length;
    // 2026-05-30 boss decision: 「直接拿掉所有教學 gate」. The banner now surfaces ONLY
    // the 7-day sign-in calendar entry — pure retention, no gameplay gate.
    //
    // Copy fix (boss「數字不一致」): this is the 每日簽到日曆 (calendar day 1..7),
    // a DIFFERENT counter from the header 🔥 連續登入 streak pill. We frame it as
    // 「第 N/7 天」so it never reads as the same number as the streak. The button
    // says 「領獎勵」when something is claimable, otherwise 「簽到日曆」.
    const claimedCount = snap.claimedDays.length;
    const subText = pending > 0
      ? `有 <strong>${pending}</strong> 天獎勵可以領`
      : `已簽到 ${claimedCount}/7 天 · 明天再來`;
    const btnLabel = pending > 0 ? '領獎勵' : '簽到日曆';
    root.innerHTML = `
      <div class="dayone-banner__inner">
        <div class="dayone-banner__icon" aria-hidden="true">📅</div>
        <div class="dayone-banner__text">
          <div class="dayone-banner__title">每日簽到</div>
          <div class="dayone-banner__sub">${subText}</div>
        </div>
        <div class="dayone-banner__actions">
          <button class="dayone-banner__btn dayone-banner__btn--calendar"
                  data-testid="dayone-banner-calendar"
                  aria-label="打開七日簽到日曆">
            ${btnLabel}
            ${pending > 0 ? `<span class="dayone-banner__badge" data-testid="dayone-banner-badge">${pending}</span>` : ''}
          </button>
        </div>
      </div>
    `;
    wire();
  }

  function wire(): void {
    const calBtn = root.querySelector('.dayone-banner__btn--calendar') as HTMLButtonElement | null;
    if (calBtn) {
      calBtn.addEventListener('click', () => {
        openCalendar();
      });
    }
    // 2026-05-30 boss decision: 教學魔王 CTA removed (see rerender). The
    // ef:start-tutorial-boss wire-up is intentionally gone so the kid can never be
    // routed into the sticky tutorial boss fight. `onStartTutorialBoss` is kept in
    // the actions interface for back-compat but is now a no-op.
  }

  function openCalendar(): void {
    if (calendarUnmount) return;
    const overlay = document.createElement('div');
    overlay.className = 'dayone-calendar-overlay';
    overlay.setAttribute('data-testid', 'dayone-calendar-overlay');
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeCalendar();
    });
    document.body.appendChild(overlay);
    calendarUnmount = renderDayOneCalendar(overlay, progress, {
      onClaim: () => rerender(),
      onClose: closeCalendar,
      // Forward the FIXED nowMs only in test mode so calendar matches banner.
      // In live-clock mode the calendar reads Date.now() itself, also fresh.
      nowMs: useLiveClock ? undefined : actions.nowMs,
    });
    const origUnmount = calendarUnmount;
    calendarUnmount = () => {
      origUnmount?.();
      overlay.remove();
    };
  }

  function closeCalendar(): void {
    if (!calendarUnmount) return;
    calendarUnmount();
    calendarUnmount = null;
    rerender();
  }

  // Initial render.
  rerender();
  container.appendChild(root);

  // First-launch gift overlay — only auto-shown when the gift is still pending
  // and the kid has just been seeded (i.e., D1 not yet claimed). We surface it
  // INSIDE renderDayOneBanner so home.ts has a single integration point.
  const snap = snapshotHook(progress, getNow());
  if (snap.showGiftOverlay) {
    overlayUnmount = mountFirstLaunchOverlay(progress, () => {
      overlayUnmount = null;
      rerender();
    }, getNow());
  }

  // QA #263 fix #2: midnight refresh tick. With live-clock mode, schedule a
  // periodic re-render so the snapshot picks up the new Day-N once midnight
  // passes. 60s cadence is cheap (rerender is a string concat + innerHTML).
  // Disabled in tests (actions.nowMs provided) so vitest's fake timers don't
  // need to clean up the setInterval.
  if (useLiveClock && refreshIntervalMs > 0 && typeof setInterval !== 'undefined') {
    refreshTimer = setInterval(() => {
      // Skip rerender if a calendar overlay is open — would race with claim UI.
      if (calendarUnmount === null) rerender();
    }, refreshIntervalMs);
  }

  return destroy;
}

/**
 * The "歡迎加入永恆要塞!" first-launch overlay. Two buttons:
 *   - 「領取禮包」 → claimDay(1) → grants D1 reward + first-launch gift bundle
 *   - 「之後再說」 → just dismisses (kid can still claim from calendar later)
 *
 * The overlay is body-mounted (not inside the banner) so it sits on top of
 * everything; on close it calls `onDone()` so the banner can re-render.
 *
 * Returns an unmount function so banner-level cleanup can tear it down if
 * the home page is unmounted mid-flight.
 */
export function mountFirstLaunchOverlay(
  progress: PlayerProgress,
  onDone: () => void,
  nowMs: number = Date.now(),
): () => void {
  const overlay = document.createElement('div');
  overlay.className = 'dayone-gift-overlay';
  overlay.setAttribute('data-testid', 'dayone-gift-overlay');
  // QA #263 fix #10: pull D1 freeXP from DAY_REWARDS instead of hard-coding
  // `+ 100`. claimDay(1) credits `FIRST_LAUNCH_GIFT_XP + DAY_REWARDS[0].freeXP`
  // (see dayone-hook.ts) so the overlay copy now matches the real grant. If
  // someone tunes D1 in DAY_REWARDS, the overlay updates automatically.
  const d1FreeXP = DAY_REWARDS.find((r) => r.day === 1)?.freeXP ?? 0;
  const totalGiftXP = FIRST_LAUNCH_GIFT_XP + d1FreeXP;
  overlay.innerHTML = `
    <div class="dayone-gift-overlay__card">
      <div class="dayone-gift-overlay__title">🎁 歡迎加入永恆要塞!</div>
      <div class="dayone-gift-overlay__body">
        <p>送你新手禮包(七日簽到的第 1 天):</p>
        <ul>
          <li>⚡ <strong>${totalGiftXP}</strong> 自由修煉值</li>
          <li>🌟 入門技能「擴散三連發」自動解鎖</li>
        </ul>
        <p class="dayone-gift-overlay__hint">領取後每天回來簽到,還有更多獎勵!</p>
      </div>
      <div class="dayone-gift-overlay__actions">
        <button class="dayone-gift-overlay__claim" data-testid="dayone-gift-claim">領取禮包</button>
        <button class="dayone-gift-overlay__later" data-testid="dayone-gift-later">之後再說</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const cleanup = (): void => {
    overlay.remove();
    onDone();
  };

  (overlay.querySelector('[data-testid="dayone-gift-claim"]') as HTMLButtonElement).addEventListener('click', () => {
    claimDay(progress, 1, nowMs);
    cleanup();
  });
  (overlay.querySelector('[data-testid="dayone-gift-later"]') as HTMLButtonElement).addEventListener('click', cleanup);

  return cleanup;
}
